import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { decodeHTML, prettyDate } from 'scripts/lib/utils'
import PlayControl from '../../images/play-control.svg'
import locale, {langPrefix} from 'shared/i18n/react'

export const ResponsiveImage = (props) => {
  const {image, imageClass, altText} = props
  const imageURL = image
  if (!imageURL) { return null }
  const sizes = [
    { width: '80', suffix: '80x80' },
    { width: '368', suffix: '368x245' },
    { width: '555', suffix: '555x370' },
    { width: '900', suffix: '900x600' }
  ]

  return <img
    src={`${props.image}?s=7x5&e=t`} data-srcset={sizes.map((item, index) => {
      return imageURL + '?s=' + item.suffix + '&e=t ' + item.width + 'w'
    }).join(', ')}
    data-src={`${imageURL}?s=900x600&e=t`}
    sizes="100vw"
    className={`responsive-img block-display lazyload ${imageClass || ''}`}
    alt={altText}
  />
}

export const PaddedImage = (props) => {
  const {containerClass} = props
  return <span className={containerClass || ''}>
    <ResponsiveImage {...props}/>
  </span>
}

export const ResponsiveIframe = (props) => {
  const { className } = props
  let currClassName = 'responsive-iframe'
  if (className) {
    currClassName += ` ${className}`
  }
  const iframeProps = Object.assign({}, props, { className: currClassName })

  return (
    <iframe { ...iframeProps } ></iframe>
  )
}

ResponsiveIframe.propTypes = {
  src: PropTypes.string.isRequired
}

export const structureLinks = (props) => {
  const urlPrefix = langPrefix({trailingSlash: true})
  const {id, slug, type} = props
  let permaLink = slug || ''
  switch (type) {
    case 'post':
      permaLink = `article/${id}/${slug}`
      break
    case 'tag':
      permaLink = `tag/${slug}`
      break
    case 'fanclub':
      permaLink = `fanclub/${slug}`
      break
    case 'category':
      permaLink = `category/${slug}`
      break
    case 'author':
      permaLink = `author/${slug}`
  }
  return `${urlPrefix}${permaLink}`
}

export function extractOrFormInitialData(key, initialData) {
  const dataAvailable = this.isDataAvailable && this.isDataAvailable[key]
  const hasError = this.isDataAvailable && this.isDataAvailable['hasError']
  const initial = (dataAvailable) ? this.isDataAvailable[key] : initialData

  if (dataAvailable || hasError) {
    delete window.__serverData__
  }

  return initial
}

// We need to fire an event to let the store know
// that the user has clicked a link; so it can clear the stored value if
// it already exists, allowing for new data to be visible.
export const customEvent = (e) => {
  if (e) {
    const event = new CustomEvent('soompi-click', {'bubbles': true, 'cancelable': true})
    e.target.dispatchEvent(event)
  }
}

export const GenerateLink = (props) => {
  let {id, slug, type, title, element, cssClass, truncate, arrow, customOnClick} = props
  if (!element) { return null }
  if (!slug && element) { return element }

  let permaLink = structureLinks({id, slug, type})
  let titleLimit = parseInt(truncate)
  const showArrow = arrow ? <span className="icon-right" /> : ''
  if (!isNaN(titleLimit) && typeof element === 'string') {
    if (element.length > titleLimit) {
      element = decodeHTML(element.substring(0, titleLimit).replace(/^[ \t]+|[ \t]+$/ig, '') + '&hellip;')
    }
  } else if (typeof element === 'string') {
    element = decodeHTML(element)
  }
  return (<Link to={`/${permaLink}`} onClick={(e) => {
    if (typeof customOnClick === 'function') { customOnClick() }
    if (type !== 'post') { customEvent(e) }
  }} className={cssClass} title={decodeHTML(title)} >
    {element}
    {showArrow}
  </Link>)
}

export const Associations = (props) => {
  const {data, assocClass, type, truncate, limit, customOnClick} = props
  if (!data) { return null }
  return data.map((item, index) => {
    if (limit && index >= limit) { return null }
    const truncateClass = truncate ? 'truncate' : ''
    const linkProps = {
      type: type,
      id: item.id,
      slug: item.slug,
      title: item.name,
      element: item.name,
      customOnClick
    }
    // This link is fake and should be replaced with slug soon as the data is available
    return <div tabIndex={0} key={`${type}-${index}`} className={`uppercase ${assocClass || ''} ${truncateClass}`}>
      <GenerateLink {...linkProps} />
    </div>
  })
}

export const PaddedAssociations = (props) => {
  const {data, containerClass} = props
  if (!data) { return false }
  return <div className={containerClass || ''}>
    <Associations {...props} />
  </div>
}

export const Headings = (props) => {
  const {heading, cssClass, content} = props
  const Tag = `h${heading}`
  let html = (typeof content === 'object') ? content : decodeHTML(content)
  return <Tag className={cssClass || ''}>{html}</Tag>
}

// Allow of display of headings without additional escaping of content
export const RawHeadings = (props) => {
  const {heading, cssClass, content} = props
  const Tag = `h${heading}`
  return <Tag className={cssClass || ''} dangerouslySetInnerHTML={{__html: content}}></Tag>
}

export const SectionTitle = (props) => {
  const {title, arrow, slug, id, type, customOnClick} = props
  if (!title) { return null }
  const content = `${title}`
  const linkProps = {
    id: id,
    slug: slug,
    cssClass: 'section-title-link',
    type: type,
    element: content,
    arrow: (arrow !== false),
    customOnClick
  }

  const sectionTitle = slug ? <GenerateLink {...linkProps} /> : decodeHTML(content)
  return <Headings heading={2} cssClass='section-title uppercase' content={sectionTitle} />
}

export const PaddedDate = ({containerClass, date}) => {
  return <div className={containerClass || ''}>{prettyDate(date, false, locale)}</div>
}

export const LoadMoreButton = (props) => {
  const {callback, status, text} = props
  return (<a className={`load-more btn btn-block ${(status === 'pressed') ? 'disabled' : ''}`} href="javascript:;" onClick={callback}>{text}</a>)
}

/* Form Switch */
export const FormSwitch = (props) => {
  const {name, checked, handleChange} = props
  return (
    <label className="switch switch-round" role="switch">
      <input key={`form-swith-${name}`} type="checkbox"
        className="switch-toggle"
        name={name}
        checked={checked}
        onChange={handleChange}
      />
      <span className="switch-label"></span>
    </label>
  )
}

export const PlayBtn = (props) => {
  const {videos} = props
  if ((videos && !videos.header.url) || !videos) return null
  return (
    <img src={PlayControl} className="play-btn" />
  )
}

export const Highlighter = (query, phrase) => {
  const sanitizedQuery = query.replace(/[#-.]|[[-^]|[?|{}]/g, '\\$&')
  const regex = new RegExp(sanitizedQuery, 'gi')
  const highlight = (word) => `<span class="highlight">${word}</span>`
  const highlightedPhrase = phrase.replace(regex, highlight)

  return (<span dangerouslySetInnerHTML={{__html: highlightedPhrase}}></span>)
}



// WEBPACK FOOTER //
// ./assets/js/helpers/AppHelper.jsx